// lib
import React, { Component } from "react";
import PropTypes from "prop-types";
import { Translation } from "react-i18next";
import * as vis from "vis-timeline/standalone";
import { Alert } from "@material-ui/lab";
import moment from "moment";
import equal from "fast-deep-equal";
// src
import { currentLocalizer, formats, messages } from "i18n/localizer";
import { setThrottledTimout } from "utils/timeout";
import { getTrackedRange } from "utils/ranges";
import Toolbar from "containers/plannings/table/partials/Toolbar";
import { DAY, HOUR } from "config/constants";
import { newWindowRange } from "./utils/processors";

// boutons UX https://visjs.github.io/vis-timeline/examples/timeline/interaction/navigationMenu.html
const VIEWS = ["day", "threeDays", "fiveDays", "week", "month", "timeline"];

const OPTIONS = {
  groupHeightMode: "fitItems",
  orientation: "top",
  margin: {
    item: 3,
  },
  max: moment().endOf("year").add(1, "y"),
  min: moment().startOf("year").subtract(1, "y"),
  verticalScroll: true,
  horizontalScroll: true,
  showWeekScale: true,
  stack: true,
  type: "range",
  zoomKey: "ctrlKey",
  zoomFriction: 24,
  zoomMax: DAY * 7,
  zoomMin: HOUR,
};

const formatTitle = (start, end) => formats().dayRangeHeaderFormat({ start, end });

export default class Timeline extends Component {
  constructor(props) {
    super(props);
    const { dateStart, dateEnd, defaultStart, defaultEnd } = this.props;
    this.state = {
      title: formatTitle(defaultStart, defaultEnd),
      trackedStart: dateStart,
      trackedEnd: dateEnd,
    };
    this.ref = React.createRef();
  }

  componentDidMount() {
    const { defaultStart: start, defaultEnd: end, items, language, rows } = this.props;
    const timeline = new vis.Timeline(this.ref.current, [], {
      ...OPTIONS,
      start,
      end,
      locale: language,
    });
    timeline.on("rangechanged", this.handleRangeChanged);
    timeline.on("select", this.handleSelect);
    this.timeline = timeline;
    this.updateTimeline({ items, rows });
  }

  componentDidUpdate({
    items: prevItems,
    language: prevLanguage,
    rows: prevRows,
    focus: prevFocus,
  }) {
    const { items, language, rows, updatedFromPicker, focus } = this.props;
    if (!equal(items, prevItems)) this.updateTimeline({ items });
    if (!equal(rows, prevRows)) this.updateTimeline({ rows });
    if (!equal(language, prevLanguage)) {
      const { start, end } = this.timeline.getWindow();
      this.timeline.setOptions({
        ...OPTIONS,
        start,
        end,
        locale: language,
      });
    }
    if (!equal(focus, prevFocus) && updatedFromPicker) {
      const { start, end } = this.timeline.getWindow();
      this.move("FOCUS", focus, end - start);
    }
  }

  componentWillUnmount() {
    this.timeline.destroy();
  }

  updateTimeline = ({ items, rows }) => {
    if (items) this.timeline.setItems(items);
    if (rows) {
      const addOptions =
        rows.length >= 10
          ? {
              orientation: "both",
              margin: {
                item: 3,
              },
            }
          : {
              orientation: "top",
              margin: {
                item: 2,
              },
            };
      const options = { ...OPTIONS, ...addOptions };
      this.timeline.setGroups(new vis.DataSet(rows));
      this.timeline.setOptions(options);
    }
  };

  updateTracked = (start, end) => {
    const { focus, updateRangeAndFetch, updatedFromPicker } = this.props;
    const { trackedStart, trackedEnd } = this.state;
    let newTrackedStart;
    let newTrackedEnd;
    let updated = false;
    if (start < trackedStart) {
      updated = true;
      const newFocus = updatedFromPicker ? focus : moment(start).subtract(7, "d");
      [newTrackedStart, newTrackedEnd] = getTrackedRange(newFocus, "timeline");
    } else if (end > trackedEnd) {
      updated = true;
      [newTrackedStart, newTrackedEnd] = getTrackedRange(end, "timeline");
    }
    if (updated) {
      updateRangeAndFetch(newTrackedStart, newTrackedEnd);
      this.setState({ trackedStart: newTrackedStart, trackedEnd: newTrackedEnd });
    }
  };

  // Triggered after timeline interaction OR after "handleNavigate->move"
  handleRangeChanged = ({ start: s, end: e }) => {
    const start = s.getTime();
    const end = e.getTime();
    const { updateTimelineWindow } = this.props;
    this.setState({
      title: formatTitle(start, end),
    });
    this.updateTracked(start, end);
    setThrottledTimout(() => updateTimelineWindow({ start, end }), 500, "timeline/move");
  };

  handleSelect = ({ items: selecteds }) => {
    if (!selecteds || !selecteds.length) return;
    const { handleOpenModal, items } = this.props;
    const { event } = items.find(({ id }) => id === selecteds[0]);
    handleOpenModal(event);
  };

  move = (action, start, end) => {
    const [newStart, newEnd] = newWindowRange(action, start, end);
    this.timeline.setWindow(newStart, newEnd);
  };

  handleNavigate = (action) => {
    const { start, end } = this.timeline.getWindow();
    const endOrDuration = action === "TODAY" ? end - start : end;
    this.move(action, start, endOrDuration);
  };

  render() {
    const { language, updateView } = this.props;
    const { title } = this.state;
    return (
      <>
        <Toolbar
          view="timeline"
          views={VIEWS}
          label={title}
          localizer={{ ...currentLocalizer(language), messages: messages(language) }}
          onNavigate={this.handleNavigate}
          onView={updateView}
        />
        <div className="timeline" ref={this.ref} />

        <Translation>
          {(t) => (
            <Alert severity="info" className="app-alert-info">
              {t("timeline.info")}
            </Alert>
          )}
        </Translation>
      </>
    );
  }
}

Timeline.propTypes = {
  // parent callback
  handleOpenModal: PropTypes.func.isRequired,
  // data props
  dateStart: PropTypes.number.isRequired,
  dateEnd: PropTypes.number.isRequired,
  focus: PropTypes.number.isRequired,
  items: PropTypes.array.isRequired,
  language: PropTypes.string.isRequired,
  // isSmall: PropTypes.bool.isRequired,
  rows: PropTypes.array.isRequired,
  updatedFromPicker: PropTypes.bool.isRequired,
  defaultEnd: PropTypes.number,
  defaultStart: PropTypes.number,
  // actions props
  updateRangeAndFetch: PropTypes.func.isRequired,
  updateTimelineWindow: PropTypes.func.isRequired,
  updateView: PropTypes.func.isRequired,
};

Timeline.defaultProps = {
  defaultEnd: null,
  defaultStart: null,
};
